import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  ShoppingCart,
  Clock,
  CheckCircle,
  DollarSign,
  ArrowRight,
  User,
  Truck,
  Boxes
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Order } from '../../types';
import apiService from '../../services/api';

interface ClientStats {
  totalOrders: number;
  pendingOrders: number;
  completedOrders: number;
  totalSpent: number;
  recentOrders: Order[];
}

const ClientDashboard: React.FC = () => {
  const { user } = useAuth();
  const isCompany = user?.clientType === 'empresa';
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchClientStats = async () => {
      try {
        const response = await apiService.getMyOrders({ page: 1, limit: 100 });
        const orders = response.orders || [];

        const totalOrders = orders.length;
        const pendingOrders = orders.filter(o => o.status === 'pendiente').length;
        const completedOrders = orders.filter(o => o.status === 'completado').length;
        const totalSpent = orders
          .filter(o => o.status === 'completado')
          .reduce((acc, o) => acc + (o.totalAmountARS || 0), 0);

        setStats({
          totalOrders,
          pendingOrders,
          completedOrders,
          totalSpent,
          recentOrders: orders.slice(0, 3)
        });
      } catch (error) {
        console.error('Error fetching client stats:', error);
        setStats({
          totalOrders: 0,
          pendingOrders: 0,
          completedOrders: 0,
          totalSpent: 0,
          recentOrders: []
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientStats();
  }, []);

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      pendiente: 'Pendiente',
      confirmado: 'Confirmado',
      en_produccion: 'En Produccion',
      completado: 'Completado',
      cancelado: 'Cancelado'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      confirmado: 'bg-blue-100 text-blue-800',
      en_produccion: 'bg-orange-100 text-orange-800',
      completado: 'bg-green-100 text-green-800',
      cancelado: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount: number) => `$${amount.toLocaleString('es-AR')} ARS`;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-300 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-300 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bienvenido, {user?.name}</h1>
          <p className="text-gray-600">Resumen real de tu cuenta</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100"><ShoppingCart className="h-6 w-6 text-blue-600" /></div>
            <div className="ml-4"><p className="text-sm font-medium text-gray-600">Total Pedidos</p><p className="text-2xl font-bold text-gray-900">{stats?.totalOrders || 0}</p></div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100"><Clock className="h-6 w-6 text-yellow-600" /></div>
            <div className="ml-4"><p className="text-sm font-medium text-gray-600">Pendientes</p><p className="text-2xl font-bold text-gray-900">{stats?.pendingOrders || 0}</p></div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100"><CheckCircle className="h-6 w-6 text-green-600" /></div>
            <div className="ml-4"><p className="text-sm font-medium text-gray-600">Completados</p><p className="text-2xl font-bold text-gray-900">{stats?.completedOrders || 0}</p></div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100"><DollarSign className="h-6 w-6 text-purple-600" /></div>
            <div className="ml-4"><p className="text-sm font-medium text-gray-600">Total Gastado</p><p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.totalSpent || 0)}</p></div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Pedidos Recientes</h2>
          <Link to="/cliente/pedidos" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center">Ver todos<ArrowRight className="ml-1 h-4 w-4" /></Link>
        </div>

        {stats?.recentOrders.length ? (
          <div className="space-y-4">
            {stats.recentOrders.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">{order.orderNumber}</p>
                  <p className="text-xs text-gray-500">{formatDate(order.createdAt)}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>{getStatusLabel(order.status)}</span>
                  <p className="text-sm font-medium text-gray-900 mt-1">{order.totalAmountARS ? formatCurrency(order.totalAmountARS) : 'A confirmar'}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Todavia no tienes pedidos.</p>
          </div>
        )}
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Acciones Rapidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {!isCompany && (
            <Link to="/cliente/productos" className="flex items-center p-4 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors">
              <Package className="h-5 w-5 text-primary-600 mr-3" />
              <span className="text-sm font-medium text-primary-700">Ver Catalogo</span>
            </Link>
          )}
          {isCompany && (
            <Link to="/cliente/stock" className="flex items-center p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
              <Boxes className="h-5 w-5 text-amber-600 mr-3" />
              <span className="text-sm font-medium text-amber-700">Mi Stock</span>
            </Link>
          )}
          <Link to="/cliente/remitos" className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
            <Truck className="h-5 w-5 text-blue-600 mr-3" />
            <span className="text-sm font-medium text-blue-700">Mis Remitos</span>
          </Link>
          <Link to="/cliente/pedidos" className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
            <ShoppingCart className="h-5 w-5 text-green-600 mr-3" />
            <span className="text-sm font-medium text-green-700">Mis Pedidos</span>
          </Link>
          <Link to="/cliente/perfil" className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
            <User className="h-5 w-5 text-purple-600 mr-3" />
            <span className="text-sm font-medium text-purple-700">Mi Perfil</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
